import { call, put, takeLatest } from 'redux-saga/effects';
import { t } from 'i18next';

import { Api } from '../../../entries/ApiTransport';
import sagaAssessor from '../../../utils/sagaAssessor';
import { clientTypes, clientActions } from './actions';
import { toastActions } from 'Containers/Toasts/store';
import {
  getDemoBadClients,
  getDemoClientProfile,
  getDemoClientsList,
} from 'demo';
import { getLiveDemoBundle, selectIsDemoMode } from 'demo/saga';
import { addDemoClient, getExtraClients, nextDemoId } from 'demo/session';
import { applyInviteQuotaFromApi } from '../utils/applyInviteQuota';

const api = Api.getInstance();

let clientsPlusRequestSeq = 0;
let clientsStatsRequestSeq = 0;

const getClients = ({ payload, callback, options }) =>
  sagaAssessor(
    () =>
      function* () {
        if (yield* selectIsDemoMode()) {
          const bundle = yield* getLiveDemoBundle();
          yield put(clientActions.GET_CLIENTS.SUCCESS({
            clienti: [...(bundle.clients || []), ...getExtraClients()],
          }));
          return;
        }
        const { data } = yield call(() => api.get('/business/clienti/getAll'));
        yield put(clientActions.GET_CLIENTS.SUCCESS({
          clienti: data?.clienti,
          inviti_onboarding: data?.inviti_onboarding,
          inviti_onboarding_tot: data?.inviti_onboarding_tot,
        }));
        yield* applyInviteQuotaFromApi(data);
      },
    (err) => clientActions.GET_CLIENTS.FAILED(err),
    options,
  );

const getClientsPlus = ({ payload, callback, options }) =>
  sagaAssessor(
    () =>
      function* () {
        const requestId = ++clientsPlusRequestSeq;
        try {
          const {
            page = 1,
            limit = 20,
            search = '',
            filter = 'all',
          } = payload || {};
          if (yield* selectIsDemoMode()) {
            const bundle = yield* getLiveDemoBundle();
            const data = getDemoClientsList(bundle, { page, limit, search, filter });
            if (requestId !== clientsPlusRequestSeq) return;
            yield put(clientActions.GET_CLIENTS_PLUS.SUCCESS(data));
            return;
          }
          const params = new URLSearchParams();
          params.set('page', String(Math.max(1, Number(page) || 1)));
          params.set('limit', String(Math.min(50, Math.max(1, Number(limit) || 20))));
          if (search) params.set('search', String(search));
          if (filter) params.set('filter', String(filter));

          const { data } = yield call(() =>
            api.get(`/business/clienti/list?${params.toString()}`),
          );
          if (requestId !== clientsPlusRequestSeq) return;

          yield put(clientActions.GET_CLIENTS_PLUS.SUCCESS(data));
          yield* applyInviteQuotaFromApi(data);

          const ids = (Array.isArray(data?.clienti) ? data.clienti : [])
            .map((client) => client?.idutente ?? client?.id)
            .filter((id) => id != null && id !== '');
          if (ids.length) {
            yield put(
              clientActions.GET_CLIENTS_COUNTERS.REQUEST({ ids }, null, { skipLoader: true }),
            );
          }
        } finally {
          if (requestId === clientsPlusRequestSeq) {
            callback && typeof callback === 'function' && callback();
          }
        }
      },
    (err) => clientActions.GET_CLIENTS_PLUS.FAILED(err),
    options,
  );

const getClientsStats = ({ payload, callback, options }) =>
  sagaAssessor(
    () =>
      function* () {
        const requestId = ++clientsStatsRequestSeq;
        try {
          const { search = '', filter = 'all' } = payload || {};
          if (yield* selectIsDemoMode()) {
            const bundle = yield* getLiveDemoBundle();
            const data = getDemoClientsList(bundle, { search, filter, page: 1, limit: 1 });
            if (requestId !== clientsStatsRequestSeq) return;
            yield put(clientActions.GET_CLIENTS_STATS.SUCCESS({ stats: data.stats }));
            return;
          }
          const params = new URLSearchParams();
          if (filter) params.set('filter', String(filter));
          if (search) params.set('search', String(search));
          const qs = params.toString();
          const { data } = yield call(() =>
            api.get(`/business/clienti/stats${qs ? `?${qs}` : ''}`),
          );
          if (requestId !== clientsStatsRequestSeq) return;
          yield put(clientActions.GET_CLIENTS_STATS.SUCCESS(data));
        } finally {
          if (requestId === clientsStatsRequestSeq) {
            callback && typeof callback === 'function' && callback();
          }
        }
      },
    (err) => clientActions.GET_CLIENTS_STATS.FAILED(err),
    options,
  );

const getClientsCounters = ({ payload, callback, options }) =>
  sagaAssessor(
    () =>
      function* () {
        try {
          const ids = Array.isArray(payload?.ids) ? payload.ids : [];
          if (!ids.length) return;
          if (yield* selectIsDemoMode()) {
            const bundle = yield* getLiveDemoBundle();
            const byId = Object.fromEntries(
              [...(bundle.clients || []), ...getExtraClients()].map((client) => [
                String(client.idutente),
                client,
              ]),
            );
            const counters = ids.map((id) => ({
              idutente: id,
              totPren: byId[String(id)]?.totPren,
              lastPren: byId[String(id)]?.lastPren,
            }));
            yield put(clientActions.GET_CLIENTS_COUNTERS.SUCCESS({ counters }));
            return;
          }
          const params = new URLSearchParams();
          params.set('ids', ids.join(','));
          const { data } = yield call(() =>
            api.get(`/business/clienti/counters?${params.toString()}`),
          );
          yield put(clientActions.GET_CLIENTS_COUNTERS.SUCCESS(data));
        } finally {
          callback && typeof callback === 'function' && callback();
        }
      },
    (err) => clientActions.GET_CLIENTS_COUNTERS.FAILED(err),
    options,
  );

const getClient = ({ payload, callback }) =>
  sagaAssessor(
    () =>
      function* () {
        if (yield* selectIsDemoMode()) {
          const bundle = yield* getLiveDemoBundle();
          yield put(clientActions.GET_CLIENT.SUCCESS(getDemoClientProfile(bundle, payload.idcliente)));
          callback && typeof callback === 'function' && callback();
          return;
        }
        const {
          data: { profilo },
        } = yield call(() => api.post('/business/clienti/getCliente', payload));
        yield put(clientActions.GET_CLIENT.SUCCESS(profilo));
        callback && typeof callback === 'function' && callback();
      },
    (err) => clientActions.GET_CLIENT.FAILED(err),
  );
const addClient = ({ payload, callback }) =>
  sagaAssessor(
    () =>
      function* () {
        if (yield* selectIsDemoMode()) {
          const user = {
            idutente: nextDemoId(),
            nome: payload.nome,
            cognome: payload.cognome,
            email: payload.email,
            cellulare: payload.cellulare,
            tipo: 0,
            totPren: 0,
            lastPren: null,
            createFromSalone: 1,
            cellulare_verificato: 0,
            disabilitato: 0,
            ultimo_accesso: new Date().toISOString(),
          };
          addDemoClient(user);
          yield put(clientActions.ADD_CLIENT.SUCCESS({ user }));
          callback && typeof callback === 'function' && callback();
          yield put(
            toastActions.OPEN_TOAST.SUCCESS({
              type: 'clients',
              props: { title: t('common.toastMessages.clientAdded') },
            }),
          );
          return;
        }
        const { data } = yield call(() => api.post('/business/clienti/crea', payload));
        yield put(clientActions.ADD_CLIENT.SUCCESS(data));
        callback && typeof callback === 'function' && callback();

        yield put(
          toastActions.OPEN_TOAST.SUCCESS({
            type: 'clients',
            props: { title: t('common.toastMessages.clientAdded') },
          }),
        );
      },
    (err) => clientActions.ADD_CLIENT.FAILED(err),
  );

const editClient = ({ payload, callback }) =>
  sagaAssessor(
    () =>
      function* () {
        if (yield* selectIsDemoMode()) {
          yield put(clientActions.EDIT_CLIENT.SUCCESS(payload));
          callback && typeof callback === 'function' && callback();
          return;
        }
        const { data } = yield call(() =>
          api.post('/business/clienti/aggiorna', { user: payload }),
        );
        yield put(clientActions.EDIT_CLIENT.SUCCESS(payload));
        callback && typeof callback === 'function' && callback();
      },
    (err) => clientActions.EDIT_CLIENT.FAILED(err),
  );

const editClientphone = ({ payload, callback }) =>
  sagaAssessor(
    () =>
      function* () {
        if (yield* selectIsDemoMode()) {
          yield put(clientActions.EDIT_CLIENT_PHONE.SUCCESS(payload));
          callback && typeof callback === 'function' && callback();
          return;
        }
        const { data } = yield call(() => api.post('/business/clienti/aggiorna_numero', payload));
        yield put(clientActions.EDIT_CLIENT_PHONE.SUCCESS(payload));
        callback && typeof callback === 'function' && callback();
      },
    (err) => clientActions.EDIT_CLIENT_PHONE.FAILED(err),
  );

const editClientPassword = ({ payload, callback }) =>
  sagaAssessor(
    () =>
      function* () {
        if (yield* selectIsDemoMode()) {
          yield put(clientActions.EDIT_CLIENT_PASSWORD.SUCCESS(payload));
          callback && typeof callback === 'function' && callback();
          yield put(
            toastActions.OPEN_TOAST.SUCCESS({
              type: 'clients',
              props: { title: 'Password cambiata con successo!' },
            }),
          );
          return;
        }
        const { data } = yield call(() => api.post('/business/clienti/aggiorna_password', payload));
        yield put(clientActions.EDIT_CLIENT_PASSWORD.SUCCESS(payload));
        callback && typeof callback === 'function' && callback();

        yield put(
          toastActions.OPEN_TOAST.SUCCESS({
            type: 'clients',
            props: { title: 'Password cambiata con successo!' },
          }),
        );
      },
    (err) => clientActions.EDIT_CLIENT_PASSWORD.FAILED(err),
  );

const addNote = ({ payload, callback }) =>
  sagaAssessor(
    () =>
      function* () {
        if (yield* selectIsDemoMode()) {
          yield put(clientActions.ADD_NOTE.SUCCESS(payload));
          callback && typeof callback === 'function' && callback();
          return;
        }
        const { data } = yield call(() => api.post('/business/clienti/nota', payload));
        yield put(clientActions.ADD_NOTE.SUCCESS(payload));
        callback && typeof callback === 'function' && callback();
      },
    (err) => clientActions.ADD_NOTE.FAILED(err),
  );

const editNote = ({ payload, callback }) =>
  sagaAssessor(
    () =>
      function* () {
        if (yield* selectIsDemoMode()) {
          yield put(clientActions.EDIT_NOTE.SUCCESS(payload));
          callback && typeof callback === 'function' && callback();
          return;
        }
        const { data } = yield call(() => api.put('/business/clienti/nota', payload));
        yield put(clientActions.EDIT_NOTE.SUCCESS(payload));
        callback && typeof callback === 'function' && callback();
      },
    (err) => clientActions.EDIT_NOTE.FAILED(err),
  );

const deleteNote = ({ payload, callback }) =>
  sagaAssessor(
    () =>
      function* () {
        if (yield* selectIsDemoMode()) {
          yield put(clientActions.DELETE_NOTE.SUCCESS(payload));
          callback && typeof callback === 'function' && callback();
          return;
        }
        const { data } = yield call(() => api.post('/business/clienti/delete/nota', payload));
        yield put(clientActions.DELETE_NOTE.SUCCESS(payload));
        callback && typeof callback === 'function' && callback();
      },
    (err) => clientActions.DELETE_NOTE.FAILED(err),
  );

const deleteClient = ({ payload, callback }) =>
  sagaAssessor(
    () =>
      function* () {
        if (yield* selectIsDemoMode()) {
          yield put(clientActions.DELETE_CLIENT.SUCCESS(payload.idUtente));
          callback && typeof callback === 'function' && callback();
          return;
        }
        yield call(() => api.post('business/clienti/disabilita', payload));
        yield put(clientActions.DELETE_CLIENT.SUCCESS(payload.idUtente));
        callback && typeof callback === 'function' && callback();
      },
    (err) => clientActions.DELETE_CLIENT.FAILED(err),
  );

const enableClient = ({ payload, callback }) =>
  sagaAssessor(
    () =>
      function* () {
        if (yield* selectIsDemoMode()) {
          yield put(clientActions.ENABLE_CLIENT.SUCCESS(payload.idUtente));
          callback && typeof callback === 'function' && callback();
          return;
        }
        yield call(() => api.post('business/clienti/abilita', payload));
        yield put(clientActions.ENABLE_CLIENT.SUCCESS(payload.idUtente));
        callback && typeof callback === 'function' && callback();
      },
    (err) => clientActions.ENABLE_CLIENT.FAILED(err),
  );

const deleteClientAppointment = ({ payload, callback }) =>
  sagaAssessor(
    () =>
      function* () {
        if (yield* selectIsDemoMode()) {
          yield put(clientActions.DELETE_CLIENT_APPOINTMENT.SUCCESS(payload.idPrenotazione));
          callback && typeof callback === 'function' && callback();
          return;
        }
        yield call(() => api.post('business/prenotazioni/disable', payload));
        yield put(clientActions.DELETE_CLIENT_APPOINTMENT.SUCCESS(payload.idPrenotazione));
        callback && typeof callback === 'function' && callback();
      },
    (err) => clientActions.DELETE_CLIENT_APPOINTMENT.FAILED(err),
  );

const getPrenotazioneByID = ({ payload, callback }) =>
  sagaAssessor(
    () =>
      function* () {
        const { idPrenotazione } = payload;
        if (yield* selectIsDemoMode()) {
          yield put(clientActions.GET_PRENOTAZIONE_BY_ID.SUCCESS({ idPrenotazione }));
          callback && typeof callback === 'function' && callback();
          return;
        }
        const { data } = yield call(() =>
          api.get(`prenotazioni/getPrenotazioneById?idPrenotazione=${idPrenotazione}`),
        );
        yield put(clientActions.GET_PRENOTAZIONE_BY_ID.SUCCESS(data));
        callback && typeof callback === 'function' && callback();
      },
    (err) => clientActions.GET_PRENOTAZIONE_BY_ID.FAILED(err),
  );

  //get clienti con più di 3 prenotazioni disdette all'ultimo momento
  const getBadClients = ({ payload, callback, options } = {}) => 
  sagaAssessor(
    () =>
      function* () {
        if (yield* selectIsDemoMode()) {
          const bundle = yield* getLiveDemoBundle();
          yield put(clientActions.GET_BAD_CLIENTS.SUCCESS({ clienti: getDemoBadClients(bundle) }));
          callback?.();
          return;
        }
        const { data } = yield call(() => api.get('/business/clienti/badUsers'));
        yield put(clientActions.GET_BAD_CLIENTS.SUCCESS(data));
        callback?.();
      },
    (err) => clientActions.GET_BAD_CLIENTS.FAILED(err),
    options,
  );

export default function* () {
  yield takeLatest(clientTypes.GET_CLIENTS.REQUEST, getClients);
  yield takeLatest(clientTypes.GET_CLIENTS_PLUS.REQUEST, getClientsPlus);
  yield takeLatest(clientTypes.GET_CLIENTS_STATS.REQUEST, getClientsStats);
  yield takeLatest(clientTypes.GET_CLIENTS_COUNTERS.REQUEST, getClientsCounters);
  yield takeLatest(clientTypes.GET_CLIENT.REQUEST, getClient);
  yield takeLatest(clientTypes.ADD_CLIENT.REQUEST, addClient);
  yield takeLatest(clientTypes.EDIT_CLIENT.REQUEST, editClient);
  yield takeLatest(clientTypes.EDIT_CLIENT_PHONE.REQUEST, editClientphone);
  yield takeLatest(clientTypes.EDIT_CLIENT_PASSWORD.REQUEST, editClientPassword);
  yield takeLatest(clientTypes.GET_BAD_CLIENTS.REQUEST, getBadClients);

  yield takeLatest(clientTypes.DELETE_CLIENT.REQUEST, deleteClient);
  yield takeLatest(clientTypes.ENABLE_CLIENT.REQUEST, enableClient);

  yield takeLatest(clientTypes.DELETE_CLIENT_APPOINTMENT.REQUEST, deleteClientAppointment);
  yield takeLatest(clientTypes.ADD_NOTE.REQUEST, addNote);
  yield takeLatest(clientTypes.EDIT_NOTE.REQUEST, editNote);
  yield takeLatest(clientTypes.DELETE_NOTE.REQUEST, deleteNote);
  yield takeLatest(clientTypes.GET_PRENOTAZIONE_BY_ID.REQUEST, getPrenotazioneByID);
}
